// components/tracking/VehicleList.jsx
import React from "react";
import { Truck, MapPin, Navigation, Map, Signal, Globe } from "lucide-react";

const VehicleList = ({
  vehicles,
  selectedVehicle,
  setSelectedVehicle,
  setHoveredVehicle,
  loading,
}) => {
  const getStatusConfig = (status) => {
    const configs = {
      active: {
        color: "green",
        label: "Active",
        gradient: "from-green-500 to-emerald-500",
      },
      inactive: {
        color: "red",
        label: "Inactive",
        gradient: "from-red-500 to-rose-500",
      },
      loading: {
        color: "yellow",
        label: "Loading",
        gradient: "from-yellow-500 to-orange-500",
      },
      completed: {
        color: "gray",
        label: "Completed",
        gradient: "from-gray-500 to-slate-500",
      },
      unknown: {
        color: "gray",
        label: "Unknown",
        gradient: "from-gray-500 to-slate-500",
      },
    };
    return configs[status] || configs.unknown;
  };

  const getSignalStrength = (lastUpdate) => {
    const timeDiff = Date.now() - new Date(lastUpdate).getTime();
    if (timeDiff < 300000)
      return { strength: "excellent", color: "text-green-500", bars: 4 };
    if (timeDiff < 900000)
      return { strength: "good", color: "text-yellow-500", bars: 3 };
    if (timeDiff < 1800000)
      return { strength: "fair", color: "text-orange-500", bars: 2 };
    return { strength: "poor", color: "text-red-500", bars: 1 };
  };

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden hover:shadow-lg transition-all duration-300">
      <div className="p-4 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-white sticky top-0 z-10">
        <h3 className="text-lg font-semibold text-gray-900">
          Vehicles ({vehicles.length})
        </h3>
        <p className="text-sm text-gray-600 mt-0.5">Click to view details</p>
      </div>

      <div className="max-h-[calc(600px-80px)] overflow-y-auto custom-scrollbar">
        <div className="p-4 space-y-3">
          {vehicles.length > 0 ? (
            vehicles.map((vehicle, index) => {
              const statusConfig = getStatusConfig(vehicle.status);
              const signal = getSignalStrength(vehicle.lastUpdate);
              const isSelected = selectedVehicle?.id === vehicle.id;

              return (
                <div
                  key={vehicle.id}
                  onClick={() => setSelectedVehicle(vehicle)}
                  onMouseEnter={() => setHoveredVehicle(vehicle)}
                  onMouseLeave={() => setHoveredVehicle(null)}
                  className={`group relative bg-white rounded-xl border-2 p-4 cursor-pointer transition-all duration-300 ${
                    isSelected
                      ? "border-blue-500 shadow-lg scale-[1.02]"
                      : "border-gray-200 hover:border-blue-300 hover:shadow-md"
                  }`}
                  style={{
                    animationDelay: `${index * 50}ms`,
                    animation: "fadeSlideIn 0.4s ease-out forwards",
                  }}
                >
                  {/* Gradient overlay on hover */}
                  <div
                    className={`absolute inset-0 bg-gradient-to-br ${statusConfig.gradient} opacity-0 group-hover:opacity-5 rounded-xl transition-opacity duration-300`}
                  ></div>

                  <div className="relative">
                    {/* Header */}
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center space-x-3">
                        <div
                          className={`p-2 rounded-lg bg-gradient-to-br ${statusConfig.gradient} shadow-md group-hover:scale-110 transition-transform duration-300`}
                        >
                          <Truck className="w-5 h-5 text-white" />
                        </div>
                        <div>
                          <h4 className="font-semibold text-gray-900 group-hover:text-blue-600 transition-colors">
                            {vehicle.number}
                          </h4>
                          <p className="text-sm text-gray-600">
                            {vehicle.driver}
                          </p>
                        </div>
                      </div>

                      <div className="flex items-center space-x-2">
                        <Signal className={`w-4 h-4 ${signal.color}`} />
                        <span
                          className={`px-2.5 py-1 rounded-full text-xs font-medium bg-gradient-to-r ${statusConfig.gradient} text-white shadow-sm`}
                        >
                          {statusConfig.label}
                        </span>
                      </div>
                    </div>

                    {/* Location */}
                    <div className="flex items-start space-x-2 mb-3 group/location">
                      <MapPin className="w-4 h-4 text-gray-400 mt-0.5 group-hover/location:text-blue-500 transition-colors" />
                      <span className="text-sm text-gray-600 line-clamp-2 flex-1">
                        {vehicle.position.address}
                      </span>
                    </div>

                    {/* Stats */}
                    <div className="flex items-center justify-between text-sm mb-3">
                      <div className="flex items-center space-x-1 text-gray-600">
                        <Navigation className="w-4 h-4" />
                        <span>{vehicle.speed.toFixed(1)} km/h</span>
                      </div>
                      <div className="flex items-center space-x-1 text-gray-600">
                        <Map className="w-4 h-4" />
                        <span>
                          {vehicle.route.distance.completed.toFixed(2)} km
                        </span>
                      </div>
                    </div>

                    {/* Footer */}
                    <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                      <div className="text-xs text-gray-500">
                        {vehicle.lastTrackingTime}
                      </div>
                      {vehicle.trackingLink &&
                        vehicle.trackingLink !== "No active trips." && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              window.open(vehicle.trackingLink, "_blank");
                            }}
                            className="flex items-center space-x-1 text-xs text-blue-600 hover:text-blue-700 font-medium transition-colors"
                          >
                            <Globe className="w-3 h-3" />
                            <span>Track Live</span>
                          </button>
                        )}
                    </div>

                    {/* Stop time alert */}
                    {vehicle.stopTime && (
                      <div className="mt-2 px-2 py-1 bg-red-50 border border-red-200 rounded-lg">
                        <p className="text-xs text-red-600 font-medium">
                          ⏱️ Stopped: {vehicle.stopTime}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              );
            })
          ) : (
            <div className="text-center py-12">
              <Truck className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-600 font-medium">No vehicles found</p>
              <p className="text-sm text-gray-500 mt-1">
                Try adjusting your filters
              </p>
            </div>
          )}
        </div>
      </div>

      <style jsx>{`
        @keyframes fadeSlideIn {
          from {
            opacity: 0;
            transform: translateX(-20px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }

        .custom-scrollbar::-webkit-scrollbar {
          width: 6px;
        }

        .custom-scrollbar::-webkit-scrollbar-track {
          background: #f1f5f9;
          border-radius: 10px;
        }

        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: linear-gradient(to bottom, #3b82f6, #06b6d4);
          border-radius: 10px;
        }

        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: linear-gradient(to bottom, #2563eb, #0891b2);
        }
      `}</style>
    </div>
  );
};

export default VehicleList;
